import { ADMIN_GROUP_ALIAS, isAdminRole, type Role } from '@hm/contracts'
import { Link, useRouterState } from '@tanstack/react-router'
import { LogOut } from 'lucide-react'
import type { ReactNode } from 'react'
import { GROUP_LABELS, NAV_GROUPS, type NavItem, navFor, tabsFor } from '~/app/nav'
import { HexAvatar, Lockup } from '~/components/brand'
import { LanguageSwitch } from '~/components/language-switch'
import { NotificationBell } from '~/components/notification-bell'
import { ThemeSwitch } from '~/components/theme-switch'
import { GuideButton, GuideLink } from '~/components/tour/launcher'
import { Button } from '~/components/ui/button'
import { useLabels, useT } from '~/lib/i18n'
import { useLogout, useRememberLocale, useSession } from '~/lib/session'
import { anchor, navAnchor } from '~/lib/tour/anchors'
import { cn, initialsOf } from '~/lib/utils'

export function AppShell({ children }: { children: ReactNode }) {
  const user = useSession()
  const items = navFor(user)
  const pathname = useRouterState({ select: (s) => s.location.pathname })

  return (
    <div className="flex h-full flex-col lg:flex-row">
      <Sidebar items={items} />
      <div className="flex min-w-0 flex-1 flex-col">
        <MobileHeader />
        <main className="blueprint flex-1 overflow-y-auto pb-24 lg:pb-0">
          <div
            // Keyed on the route so each screen animates in rather than snapping.
            key={pathname}
            className="mx-auto w-full max-w-5xl animate-rise px-4 py-6 sm:px-6 sm:py-9"
          >
            {children}
          </div>
        </main>
      </div>
      <MobileTabBar items={items} />
    </div>
  )
}

function useIsActive(to: string): boolean {
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  if (to === '/') return pathname === '/'
  // `/catalog` must not light up on `/catalog/categories`' behalf and vice versa,
  // so a deeper route only matches on a segment boundary.
  return pathname === to || pathname.startsWith(`${to}/`)
}

/** Admins belong to the desk, not to a business — the alias is what they see. */
function useContextLine(role: Role, orgName: string | null | undefined): string {
  const L = useLabels()
  return isAdminRole(role) ? ADMIN_GROUP_ALIAS : (orgName ?? L.role[role])
}

function Sidebar({ items }: { items: NavItem[] }) {
  const L = useLabels()
  const t = useT()
  const user = useSession()
  const logout = useLogout()
  const rememberLocale = useRememberLocale()

  return (
    <aside className="hidden w-[17rem] shrink-0 flex-col border-r border-border bg-surface lg:flex">
      <div className="flex items-center justify-between gap-2 border-b border-border px-5 py-5">
        <Lockup subtitle={useContextLine(user.role, user.orgName)} className="min-w-0" />
        <NotificationBell />
      </div>

      <nav {...anchor('shell.nav')} className="flex-1 space-y-4 overflow-y-auto px-3 py-4">
        {NAV_GROUPS.map((group) => {
          const groupItems = items.filter((i) => i.group === group)
          if (groupItems.length === 0) return null
          return (
            <div key={group} className="space-y-1">
              <p className="stencil px-3 pb-1 text-ink-subtle">{t(GROUP_LABELS[group])}</p>
              {groupItems.map((item) => (
                <SidebarLink key={item.to} item={item} />
              ))}
            </div>
          )
        })}
      </nav>

      <div className="space-y-3 border-t border-border p-3">
        <GuideLink />
        {/*
          Language sits beside the theme rather than only in Settings: somebody who
          has landed in the wrong one cannot read the way to the screen that would
          fix it. Theme is sized to its icons so the two names get the rest.
        */}
        <div className="flex gap-2">
          <ThemeSwitch {...anchor('shell.theme')} className="shrink-0" />
          <LanguageSwitch
            {...anchor('shell.language')}
            className="min-w-0 flex-1"
            onPersist={rememberLocale}
          />
        </div>
        <div
          {...anchor('shell.account')}
          className="flex items-center gap-3 rounded-control bg-surface-muted px-2.5 py-2"
        >
          <HexAvatar initials={initialsOf(user.name)} />
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-semibold text-ink">{user.name}</p>
            <p className="stencil mt-1 truncate text-ink-subtle">{L.role[user.role]}</p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="size-9 shrink-0"
            icon={<LogOut className="size-4" />}
            loading={logout.isPending}
            onClick={() => logout.mutate()}
            aria-label={t('common.sign_out')}
            title={t('common.sign_out')}
          />
        </div>
      </div>
    </aside>
  )
}

function SidebarLink({ item }: { item: NavItem }) {
  const t = useT()
  const active = useIsActive(item.to)
  const Icon = item.icon
  return (
    <Link
      {...anchor(navAnchor(item.to))}
      to={item.to}
      className={cn(
        'relative flex items-center gap-3 rounded-control py-2.5 pr-3 pl-4 text-sm font-semibold',
        'transition-[background-color,color,transform] duration-150',
        // The amber rail is the single consistent "you are here" cue across the app.
        'before:absolute before:top-1/2 before:left-0 before:h-5 before:w-[3px] before:-translate-y-1/2',
        'before:rounded-r-full before:bg-primary before:transition-transform before:duration-200',
        active
          ? 'bg-primary-soft text-ink before:scale-y-100'
          : 'text-ink-muted before:scale-y-0 hover:translate-x-0.5 hover:bg-surface-muted hover:text-ink',
      )}
      aria-current={active ? 'page' : undefined}
    >
      <Icon className={cn('size-4.5 shrink-0', active && 'text-primary')} />
      {t(item.label)}
    </Link>
  )
}

function MobileHeader() {
  const t = useT()
  const user = useSession()
  return (
    <header className="frosted sticky top-0 z-30 flex items-center justify-between gap-3 border-b border-border px-4 py-2.5 lg:hidden">
      <Lockup subtitle={useContextLine(user.role, user.orgName)} className="min-w-0" />
      <div className="flex shrink-0 items-center gap-1">
        <GuideButton />
        <NotificationBell />
        <Link to="/settings" aria-label={t('common.settings')}>
          <HexAvatar initials={initialsOf(user.name)} />
        </Link>
      </div>
    </header>
  )
}

function MobileTabBar({ items }: { items: NavItem[] }) {
  const tabs = tabsFor(items)
  return (
    <nav
      {...anchor('shell.nav')}
      className="frosted fixed inset-x-0 bottom-0 z-30 flex border-t border-border lg:hidden"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      {tabs.map((item) => (
        <TabLink key={item.to} item={item} />
      ))}
    </nav>
  )
}

function TabLink({ item }: { item: NavItem }) {
  const t = useT()
  const active = useIsActive(item.to)
  const Icon = item.icon
  return (
    <Link
      {...anchor(navAnchor(item.to))}
      to={item.to}
      className={cn(
        'relative flex min-h-15 flex-1 flex-col items-center justify-center gap-1 px-1 pt-1 text-[0.625rem] font-bold',
        'before:absolute before:top-0 before:h-[3px] before:w-9 before:rounded-b-full before:bg-primary',
        'before:transition-transform before:duration-200',
        active ? 'text-primary before:scale-x-100' : 'text-ink-subtle before:scale-x-0',
      )}
      aria-current={active ? 'page' : undefined}
    >
      <Icon className={cn('size-5 transition-transform duration-200', active && 'scale-110')} />
      <span className="w-full truncate text-center">{t(item.label)}</span>
    </Link>
  )
}
